// @ts-nocheck
import { toast } from "@/lib/toast";
import { executionKeys } from '@/features/execution/hooks/useExecutionData';
import { executionNotificationService } from '@/features/execution/services/notification.service';

interface HandleExecutionUpdatesOptions {
  data: any;
  queryClient: any;
  profileId: number;
  websocketData: {
    setWebsocketData: (profileId: number, status: "connecting" | "connected" | "disconnected" | "working" | "reconnecting" | "error") => void;
    getWebsocketData: (profileId: number) => any;
    updateWebsocketMessage?: (profileId: number, messageType: any) => void;
    getAllWebsocketData?: () => any;
  };
}

const asRecord = (value: unknown): Record<string, any> | null =>
  value !== null && typeof value === "object"
    ? (value as Record<string, any>)
    : null;

/**
 * Handle execution-channel WebSocket messages (BUG-A3).
 *
 * The backend strategy runner (services/execution/runner.py) is the source of
 * truth for the event vocabulary — it publishes exactly nine `runner.*`
 * events on the ws:execution Redis channel, forwarded verbatim by
 * redis_subscriber._route_execution_message as
 * `{ type: "runner.X", channel: "execution", payload: {...} }`.
 *
 * The previous switch matched a legacy vocabulary
 * (execution_status_update / execution_completed / …) that NO backend code
 * emits — every real event fell into `default`, execution surfaces went
 * stale for 5 minutes, and the safety events (risk_alert / naked_position /
 * execution_halted) were invisible to the user.
 */
export const handleExecutionUpdates = ({
  data,
  queryClient,
  profileId,
  websocketData
}: HandleExecutionUpdatesOptions): void => {
  try {
    const payload = asRecord(data?.payload) ?? asRecord(data?.data) ?? {};

    switch (data.type) {
      // -- Progress events: cheap invalidations; the queries re-fetch truth.
      case 'runner.state_update':
      case 'runner.order_placed':
      case 'runner.trade_closed':
        queryClient.invalidateQueries({ queryKey: executionKeys.all });
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        break;

      // -- Failure: the pipeline errored mid-run.
      case 'runner.pipeline_error':
        queryClient.invalidateQueries({ queryKey: executionKeys.all });
        executionNotificationService.handleExecutionFailure(
          String(payload.runner_id ?? 'unknown'),
          'auto',
          String(payload.error ?? 'Pipeline error'),
        );
        break;

      // -- Safety events: MUST be user-visible (Sonner), never silent.
      case 'runner.risk_alert': {
        const severity = String(payload.severity ?? '').toUpperCase();
        const title = `Risk Alert: ${payload.alert_type ?? 'risk limit'}`;
        const description = String(
          payload.message ?? 'A risk limit was triggered.',
        );
        if (severity === 'CRITICAL' || severity === 'ERROR') {
          toast.error(title, { description });
        } else {
          toast.warning(title, { description });
        }
        queryClient.invalidateQueries({ queryKey: ['risk'] });
        queryClient.invalidateQueries({ queryKey: executionKeys.all });
        break;
      }

      case 'runner.naked_position':
        // Money-path terminal: an entry filled but the bracket/flatten
        // failed — the position is unprotected. Persist until dismissed.
        toast.error('NAKED POSITION — intervention required', {
          description: String(
            payload.halted_reason ??
              'A position is open without protective orders.',
          ),
          duration: Infinity,
        });
        queryClient.invalidateQueries({ queryKey: executionKeys.all });
        queryClient.invalidateQueries({ queryKey: ['positions'] });
        break;

      case 'runner.execution_halted':
        toast.error('Execution halted', {
          description: String(
            payload.halted_reason ?? 'Execution was halted by the runner.',
          ),
          duration: Infinity,
        });
        queryClient.invalidateQueries({ queryKey: executionKeys.all });
        break;

      // -- Explicit no-ops: live office reads `agent.thought` via
      // features/office/utils/agent-stream.ts. These runner.* types are the
      // retired hook shape; handling them here would invent a second stream.
      case 'runner.signal_rejected':
      case 'runner.agent_thought':
        break;

      default:
        // Not console.warn: legacy sockets heartbeat through here ('pong')
        // and unknown types are expected during rollout.
        console.debug('Unhandled execution WebSocket message type:', data.type);
    }
  } catch (error) {
    console.error('Error handling execution WebSocket message:', error);
  }
};

/**
 * Utility function to send execution requests through WebSocket
 */
export const sendExecutionRequest = (
  socket: WebSocket | null,
  type: string,
  payload: any = {}
): void => {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    console.warn('WebSocket not connected, cannot send execution request');
    return;
  }

  try {
    const message = {
      type,
      ...payload,
      timestamp: new Date().toISOString()
    };

    socket.send(JSON.stringify(message));
  } catch (error) {
    console.error('Error sending execution WebSocket message:', error);
  }
};
